import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import {
  getUserContractDetails,
  handleSsmoContractSigned,
  getOnboardingStatus,
} from '../../../apis/merchant-onboarding-service/api';
import { Typography as T } from '../../../common/typography';
import { authenticator } from '../../wolt-auth';
import SsmoPageWrapper from '../SelfServeSignupFlow/SsmoPageWrapper';
import PenAndPaper from '../images/pen-and-paper.png';

const PageHeading = styled(T.Heading3)`
  margin-block-end: 32px;
`;

const ContentHeading = styled(T.Title1)``;

const SignYourContract = styled.div`
  padding-block-end: 10px;
`;

const Body = styled(T.Body3)`
  margin-block-end: 16px;
`;

const StyledImage = styled.img`
  width: 200px;
  height: 200px;
`;

const MainContent = styled.div`
  padding-block: 32px;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
  variant: 'blue',
})`
  width: 100%;
`;

const ContentSection = (props: { heading: string; body: string }) => {
  return (
    <>
      <ContentHeading>{props.heading}</ContentHeading>
      <div>
        <Body>{props.body}</Body>
      </div>
    </>
  );
};

/**
 * TODO:
 * - Check if contract is signed, if not then the button should go to the contract link
 * - If contract is signed then immediately hit the status-signed endpoint (which creates the merchant). On success: redirect to "Contract is signed" page
 *
 */

const SignContract = () => {
  const history = useHistory();
  const [tokenIsRefreshing, setTokenIsRefreshing] = useState(false);

  const { data: onboardingStatusData } = useQuery(['merchant-admin/me/onboarding-status'], () => getOnboardingStatus());

  const merchantId = onboardingStatusData?.merchantId;

  const { data: contractStatus, isFetching: isContractStatusFetching } = useQuery(
    ['/merchant-admin/merchants/{merchantId}/contract/signed-status', merchantId],
    () => {
      if (merchantId) {
        return handleSsmoContractSigned(merchantId);
      }
      return;
    },
    { enabled: !!merchantId },
  );

  const isContractSignedByMerchant = contractStatus?.isContractSignedByMerchant;

  const { data: contracts, isFetching: isContractDetailsLoading } = useQuery(['merchant-admin/me/contracts'], () =>
    getUserContractDetails(),
  );

  if (!contractStatus || isContractStatusFetching) {
    return (
      <SsmoPageWrapper>
        <Spinner size="large" />
      </SsmoPageWrapper>
    );
  }

  const contractUrl = contracts?.[0]?.signingUrl;

  return (
    <SsmoPageWrapper>
      <StyledImage src={PenAndPaper} />
      {isContractSignedByMerchant ? (
        <>
          <MainContent>
            <PageHeading>Contract signed!</PageHeading>
            <Body>Now, complete the remaining onboarding steps to get your venue up and running on Wolt.</Body>
          </MainContent>
          <StyledButton
            loading={tokenIsRefreshing}
            onClick={async () => {
              try {
                setTokenIsRefreshing(true);
                await authenticator.refreshUser();
                history.replace('/');
              } finally {
                setTokenIsRefreshing(false);
              }
            }}
          >
            Next
          </StyledButton>
        </>
      ) : (
        <>
          <MainContent>
            <PageHeading>Your contract is ready!</PageHeading>

            <SignYourContract>
              <ContentSection
                heading="Sign your contract"
                body="An agreement based on your selections and business information is ready for you to sign!"
              />
            </SignYourContract>

            <div>
              <ContentSection
                heading="After signing"
                body="Once signed, come back to your Wolt for Merchants page to continue your onboarding process."
              />
            </div>
          </MainContent>

          <StyledButton
            loading={isContractDetailsLoading || !contractUrl}
            onClick={() => contractUrl && window.open(contractUrl, '_blank')}
          >
            Sign Contract
          </StyledButton>
        </>
      )}
    </SsmoPageWrapper>
  );
};

export default SignContract;
